import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaPhone, FaSearch, FaTimes, FaBars } from "react-icons/fa";
import { headerContent } from "../content/common/headerContent";

const FaPhoneComponent = FaPhone as any;
const FaSearchComponent = FaSearch as any;
const FaTimesComponent = FaTimes as any;
const FaBarsComponent = FaBars as any;

const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  {/* UNCOMMENT BELOW LATER */ }
  // const { logo, phone, search, navItems } = headerContent;
  const { logo, phone, navItems } = headerContent;

  /* ---------- Close search on outside click ---------- */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest("#search-bar")) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /* ---------- Debounced Search ---------- */
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only search if query has content
    const trimmed = query.trim();
    {/* UNCOMMENT BELOW LATER */ }
    // if (trimmed.length > 0) {
    //   searchTimeoutRef.current = setTimeout(() => {
    //     navigate(`${search.redirectPath}?search=${encodeURIComponent(trimmed)}`);
    //   }, 500); 
    // }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    {/* UNCOMMENT BELOW LATER */ }
    // }, [query, navigate, search.redirectPath]);
  }, [query, navigate]);

  /* ---------- Handle Search Submit ---------- */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();

    if (trimmed) {
      // Clear any pending timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Navigate immediately on submit
      {/* UNCOMMENT BELOW LATER */ }
      // navigate(`${search.redirectPath}?search=${encodeURIComponent(trimmed)}`);
      setOpen(false);
    } else {
      // If empty, just close the search
      setOpen(false);
    }
  };

  /* ---------- Close Search ---------- */
  const closeSearch = () => {
    setQuery("");
    setOpen(false);
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  /* ---------- Toggle Search ---------- */
  const toggleSearch = () => {
    if (open) {
      closeSearch();
    } else {
      setOpen(true);
      // Focus the input after opening
      setTimeout(() => {
        const input = document.querySelector('#search-bar input') as HTMLInputElement;
        if (input) input.focus();
      }, 100);
    }
  };

  return (
    <>
      {/* ================= SEARCH BAR ================= */}
      <div
        id="search-bar"
        className={`fixed left-0 right-0 top-0 z-50 bg-[#1D1D1D] h-[72px] flex items-center px-4 transition-transform duration-300 ${open ? "translate-y-0" : "-translate-y-full"
          }`}
      >
        <FaSearchComponent className="text-white text-xl mr-3" />

        {/* UNCOMMENT BELOW LATER */}
        {/* <form onSubmit={handleSubmit} className="flex-1">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={search.placeholder}
            className="w-full bg-transparent outline-none text-white placeholder-gray-400"
          />
        </form> */}

        <FaTimesComponent
          className="text-white text-xl cursor-pointer hover:text-yellow-400 transition-colors"
          onClick={closeSearch}
        />
      </div>

      {/* ================= HEADER ================= */}
      <header
        className={`fixed left-0 right-0 z-40 bg-[#1D1D1D] text-white h-[72px] flex items-center transition-transform duration-300 ${open ? "translate-y-[72px]" : ""
          }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <img src={logo} alt="logo" className="h-12" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 text-sm">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative hover:text-yellow-400 transition-colors ${location.pathname === item.path ? "text-yellow-400" : ""
                  }`}
              >
                {item.name}

                {location.pathname === item.path && (
                  <span className="absolute left-0 -bottom-2 w-full h-[2px] bg-yellow-400"></span>
                )}
              </Link>
            ))}

            <a className="flex items-center gap-1 !cursor-auto hover:text-yellow-400 transition-colors">
              <FaPhoneComponent />
              <span>{phone.number}</span>
            </a>

            {/* UNCOMMENT BELOW LATER */}
            {/* <FaSearchComponent
              className="cursor-pointer hover:text-yellow-400 transition-colors text-lg"
              onClick={toggleSearch}
            /> */}
          </nav>

          {/* Mobile Menu Controls */}
          <div className="flex items-center gap-4 md:hidden">
            <FaSearchComponent
              onClick={toggleSearch}
              className="cursor-pointer hover:text-yellow-400 transition-colors text-lg"
            />

            <FaBarsComponent
              className="text-xl cursor-pointer hover:text-yellow-400 transition-colors"
              onClick={() => setMenuOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* ================= MOBILE MENU ================= */}
      <div
        className={`fixed top-0 left-0 w-full h-full bg-[#1D1D1D] text-white z-50 transform transition-transform duration-300 ${menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex justify-end p-6">
          <FaTimesComponent
            className="text-2xl cursor-pointer hover:text-yellow-400 transition-colors"
            onClick={() => setMenuOpen(false)}
          />
        </div>

        <div className="flex flex-col items-center gap-8 text-lg mt-10">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMenuOpen(false)}
              className={`hover:text-yellow-400 transition-colors ${location.pathname === item.path ? "text-yellow-400" : ""
                }`}
            >
              {item.name}
            </Link>
          ))}

          <a className="flex items-center gap-2 !cursor-auto hover:text-yellow-400 transition-colors">
            <FaPhoneComponent />
            <span>{phone.number}</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default Header;